import React, { memo, useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { classNames } from 'primereact/utils';

import ImageComponent from 'shared/ImageComponent';

import { openLinkInBlank, handleRedirection, replaceLawftDate, paginationDropdownOptions } from 'utils/utils';
import { convertToTimezoneFormat, handleDateTimeOffset } from 'utils/utility_functions/timezone';
import { UserDetailsContext, useUserDetailsContext } from 'context/userDetailsContext';
import { useSecureMessageContext } from '../../Context/SecureMessageContext';
import { handlePageNumberInPagination } from 'common/pagination';
import { useNavbarContext } from 'context/NavbarContext';
import { getSignedUrl } from 'modules/file_manager/services';
import { SentimentDot } from 'components/SentimentDot/SentimentDot';
import constants from 'constants/index';
import { truncateText } from 'utils/utility_functions/textTruncate';
import { Tooltip } from 'primereact/tooltip';
import { WebSocketConnectionContext } from 'context/WebSocketConnectionContext';
import GlobalTooltip from 'components/GlobalTooltip';
import moment from 'moment';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { generateFullName } from 'utils/generateFullNameUtils';

function MessageListTable({ caseMessages }) {
  const [isForward, setIsForward] = useState(true);

  const history = useHistory();
  const userContext = useContext(UserDetailsContext);
  const websocketContext = useContext(WebSocketConnectionContext);
  const { downloadFile } = useNavbarContext();

  let { id } = useParams();
  const { userDetails } = useUserDetailsContext();

  const {
    isLeadSpecific,
    isGlobalMessagesScreen,
    isFromCaseSpecificModule,
    next_Token,
    transition_Token,
    fetchCaseMessage,
    filterCaseId,
    filterMsgStatus,
    filterAuthorId,
    isFromClientPortal,
    handleMessageStatus,
    filterSentiment,
    filterParticipant,
    dateForFilter,
  } = useSecureMessageContext();

  const [pageLimit, setPageLimit] = useState(getPageLimit() ?? 10);

  function getPageLimit() {
    if (isLeadSpecific && userContext) {
      return userContext?.firmDetails?.pagination_preference?.leads?.message;
    } else if (isFromCaseSpecificModule && userContext) {
      return userContext?.firmDetails?.pagination_preference?.case?.message;
    } else if (isGlobalMessagesScreen && userContext) {
      return userContext?.firmDetails?.pagination_preference?.messages?.self;
    } else if (isFromClientPortal && userContext) {
      return userContext?.firmDetails?.pagination_preference?.client_portals?.messages?.self;
    }
  }

  function getFilePathForPagination() {
    if (isLeadSpecific && userContext) {
      return { module: 'leads', subModule: '', listName: 'message' };
    } else if (isFromCaseSpecificModule && userContext) {
      return { module: 'case', subModule: '', listName: 'message' };
    } else if (isGlobalMessagesScreen && userContext) {
      return { module: 'messages', subModule: '', listName: 'self' };
    } else if (isFromClientPortal && userContext) {
      return { module: 'client_portals', subModule: 'messages', listName: 'self' };
    }
  }

  function getMessageLink(rowData) {
    if (isFromClientPortal) {
      return rowData?.message_thread_id ? `/client/messages/${rowData?.message_thread_id}` : '/client/messages';
    } else if (isGlobalMessagesScreen) {
      return rowData?.message_thread_id ? `/messages/${rowData?.message_thread_id}` : '/messages';
    } else if (isLeadSpecific) {
      return rowData?.message_thread_id
        ? `/leads/${rowData?.case_id}/messages/${rowData?.message_thread_id}`
        : `/leads/${rowData?.case_id}/messages`;
    } else if (isFromCaseSpecificModule) {
      return rowData?.message_thread_id
        ? `/cases/${rowData?.case_id}/messages/${rowData?.message_thread_id}`
        : `/cases/${rowData?.case_id}/messages`;
    } else {
      const module = isLeadSpecific ? 'leads' : 'cases';
      return rowData?.message_thread_id
        ? `/${module}/${rowData?.case_id}/messages/${rowData?.message_thread_id}`
        : `/${module}/${rowData?.case_id}/messages`;
    }
  }

  const handleNavigateToMessageThread = (e, rowData) => {
    e.preventDefault();
    let mainModule = isFromClientPortal
      ? '/client/messages'
      : isGlobalMessagesScreen
      ? '/messages'
      : `/${isLeadSpecific ? 'leads' : 'cases'}/${rowData?.case_id}/messages`;
    let url = mainModule;
    let stateToPass = { msg: rowData };

    if (rowData?.message_thread_id) url = `${mainModule}/${rowData?.message_thread_id}`;
    if (!rowData?.is_read) handleMessageStatus(e, rowData, rowData.is_read, id);

    handleRedirection(history, url, stateToPass);
  };

  const handleNavigateToEvent = (e, attachment = [], isFrom = 'event') => {
    e.preventDefault();

    if (!attachment.length) return;
    const isEvent = isFrom === 'event';
    let url = isFromClientPortal ? '/client' : '';
    url += isEvent ? '/calendar' : '/tasks';
    const attachmentId = isEvent ? attachment[0]?.event_id : attachment[0]?.task_id;
    let queryString = `?id=${attachmentId}`;
    queryString += isEvent ? `&date=${moment(attachment[0]?.meeting_start_time).format('YYYY-MM-DD')}` : '';

    if (attachmentId) {
      history?.push({
        pathname: url,
        search: queryString,
      });
    }
  };

  const authorBodyTemplate = (rowData) => {
    const authorName = rowData?.author_details?.first_name
      ? generateFullName(rowData?.author_details)
      : rowData?.author_name || rowData?.author_details?.group_name;
    return (
      <>
        <span className="p-column-title text-break">Author</span>
        <div className="d-inline-table text-break" style={{ cursor: 'text' }}>
          <div className="d-flex flex-nowrap align-items-center ">
            <ImageComponent
              filePath={rowData?.created_by_user_image}
              fileName={authorName}
              fileSize="medium.jpg"
              avatarSize="medium"
              className={isFromClientPortal ? 'pe-none' : ''}
              style={{ cursor: 'text' }}
            />
            <div className="ms-1 message-author-ellipsis-text">
              {rowData.author_details?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
              <span title={authorName}>{authorName}</span>
            </div>
          </div>
        </div>
      </>
    );
  };

  const caseNameBodyTemplate = (rowData) => {
    function navigate() {
      let url = '';
      let stateToPass = '';

      const mainModule = rowData?.is_lead ? '/leads' : '/cases';

      url = rowData?.case_id ? `${mainModule}/${rowData?.case_id}` : mainModule;

      stateToPass = rowData;
      if (rowData?.case_name && !isFromClientPortal) {
        if (!openLinkInBlank(url, stateToPass)) {
          return url;
        }
      }
    }

    return (
      <>
        <span className="p-column-title text-break">Case Name</span>
        <Link
          to={() => navigate()}
          state={rowData}
          // className="text-primary-dark text-decoration-none case-details-link"
          className={`text-decoration-none ${classNames({
            'redirection-link-no-text-transform': rowData.case_name && !isFromClientPortal,
            'pe-none': isFromClientPortal,
          })}`}
          title={rowData?.case_name ? rowData?.case_name : undefined}
        >
          {rowData?.case_name ? rowData?.case_name : '-'}
        </Link>
      </>
    );
  };

  const stateBodyTemplate = (rowData) => {
    const { is_read, accessible_list, tag_in_accessible_list, group_users_ids } = rowData || {};
    const hasPermission =
      accessible_list?.includes(userContext?.firmDetails?.current_user) ||
      tag_in_accessible_list?.includes(userContext?.firmDetails?.current_user) ||
      group_users_ids?.includes(userContext?.firmDetails?.firm_user_id);

    return (
      <>
        <span className="p-column-title text-break">State</span>
        <div className="status-mark d-inline-table">
          <div className="status-mark d-inline-table">
            <GlobalTooltip tooltip={hasPermission ? '' : 'You are not part of this thread'}>
              <a
                href="/"
                onClick={(e) => handleMessageStatus(e, rowData, is_read, id)}
                className={`F-size12 text-decoration-none call-back ${hasPermission ? '' : 'disabled-icon'}`}
                title={rowData.is_read ? 'Mark as Unread' : 'Mark as Read'}
              >
                {rowData.is_read ? 'Mark as Unread' : <span>Mark as Read</span>}
              </a>
            </GlobalTooltip>
          </div>
        </div>
      </>
    );
  };

  const attachmentsBodyTemplate = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Attachments</span>
        <div className="upload-document d-inline-table">
          {rowData?.attachment?.length || rowData?.event_attachment?.length || rowData?.task_attachment?.length ? (
            <>
              {rowData.attachment && rowData.attachment.length ? (
                <p
                  className={`pointer text-break ${
                    Boolean(rowData?.is_read) === false || rowData?.is_new_sidebar_message === true ? 'mb-2 fw-bold' : 'text-muted'
                  }`}
                  onClick={(e) => {
                    onClickAttachments(e, rowData.attachment[0]);
                  }}
                >
                  <i className="fas fa-file-pdf redirection-link-no-text-transform"></i>
                  <span
                    className="ms-1 redirection-link-no-text-transform"
                    style={{ whiteSpace: 'normal' }}
                    title={rowData.attachment[0]?.file_name}
                  >
                    {truncateText(rowData.attachment[0]?.file_name, 20)}
                  </span>
                </p>
              ) : rowData.event_attachment && rowData.event_attachment.length ? (
                <Link
                  className={
                    rowData.is_read
                      ? 'text-muted text mb-1 text-break text-decoration-none'
                      : 'fw-bold small mb-1 text-break text-decoration-none'
                  }
                  onClick={(e) => {
                    handleNavigateToEvent(e, rowData?.event_attachment, 'event');
                  }}
                >
                  <i className="far fa-calendar" />
                  <span
                    className="ms-1 redirection-link-no-text-transform"
                    style={{ whiteSpace: 'normal' }}
                    title={rowData?.event_attachment?.[0]?.title}
                  >
                    {truncateText(rowData?.event_attachment?.[0]?.title, 20)}
                  </span>
                </Link>
              ) : rowData.task_attachment && rowData.task_attachment.length ? (
                <p
                  className={rowData.is_read ? 'text-muted small mb-1 text-break' : 'fw-bold small mb-1 text-break'}
                  onClick={(e) => {
                    handleNavigateToEvent(e, rowData?.task_attachment, 'tasks');
                  }}
                >
                  <i className="fas fa-tasks " />
                  <span
                    className="ms-1 redirection-link-no-text-transform"
                    style={{ whiteSpace: 'normal' }}
                    title={rowData?.task_attachment?.[0]?.task_name}
                  >
                    {truncateText(rowData?.task_attachment?.[0]?.task_name, 20)}
                  </span>
                </p>
              ) : null}
            </>
          ) : (
            '-'
          )}
        </div>
      </>
    );
  };

  const repliesTemplate = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Most Recent Replies</span>
        <div
          className="d-inline-table text-break"
          onClick={(e) => {
            handleNavigateToMessageThread(e, rowData);
          }}
        >
          <div>
            <p
              className="mb-1 F-size14 black-600"
              title={handleDateTimeOffset(
                userContext?.userDetails?.timezone,
                rowData?.created_at,
                constants.month_date_year_time_12_format
              )}
            >
              {handleDateTimeOffset(userContext?.userDetails?.timezone, rowData?.created_at, constants.month_date_year_time_12_format)}
            </p>
            {rowData.last_reply ? (
              <p
                className={`text-hint black-600 r-me-0 ${
                  Boolean(rowData?.is_read) === false || rowData?.is_new_sidebar_message === true ? 'mb-1' : ''
                }`}
                title={`Last Reply: ${handleDateTimeOffset(
                  userContext?.userDetails?.timezone,
                  rowData?.last_reply,
                  constants.month_date_year_time_12_format
                )}`}
              >
                Last Reply :{' '}
                {handleDateTimeOffset(userContext?.userDetails?.timezone, rowData?.last_reply, constants.month_date_year_time_12_format)}
              </p>
            ) : null}
          </div>
        </div>
      </>
    );
  };

  const subjectBodyTemplate = (rowData) => {
    // Function to remove HTML tags from the message
    const stripHtmlTags = (htmlString) => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlString;

      // Check if there are any <li> tags in the HTML
      const listItems = tempDiv.querySelectorAll('li');

      // If <li> tags are found, return them as a formatted list
      if (listItems.length > 0) {
        return Array.from(listItems)
          .map((li, index) => `${index + 1}. ${li.textContent || li.innerText}`) // Numbered list
          .join('\n'); // Use newline characters to separate items
      }

      return tempDiv.textContent || tempDiv.innerText || '';
    };
    return (
      <>
        <span className="p-column-title text-break">Subject</span>
        <div className="d-inline-table">
          <Link to={getMessageLink(rowData)} className="redirection-link-no-text-transform">
            <div className="d-flex align-items-center gap-2">
              {rowData?.scheduled_status === 'scheduled' && <i class="far fa-clock" style={{ color: 'var(--icon-red)' }}></i>}
              <div
                className={`pointer text-break text-primary-dark message-ellipsis-text flex-grow-1 ${
                  Boolean(rowData?.is_read) === false || (!isFromClientPortal && rowData?.is_new_sidebar_message === true)
                    ? 'text-bold text-capitalize'
                    : ''
                }`}
                onClick={(e) => {
                  handleNavigateToMessageThread(e, rowData);
                }}
                title={rowData?.subject}
              >
                {rowData?.subject}
              </div>
            </div>
          </Link>
          {rowData.reply_count ? (
            <>
              <span
                className={'text-hint black-600 r-me-0'}
                style={{ fontSize: 'small' }}
                title={parseInt(rowData.reply_count) > 1 ? `${parseInt(rowData.reply_count)} Replies` : `1 Reply`}
              >
                {parseInt(rowData.reply_count) > 1 ? `${parseInt(rowData.reply_count)} Replies` : `1 Reply`}
              </span>
            </>
          ) : null}
          {/* Render the plain text version of the message, truncated to 100 characters if needed */}
          {rowData?.message && (
            <div className="text-muted mt-2 d-flex align-items-center">
              {rowData?.scheduled_reply && <i className="far fa-clock me-2" style={{ color: 'var(--icon-red)' }}></i>}
              {(() => {
                const msg = replaceLawftDate(stripHtmlTags(rowData.message), userContext?.userDetails?.timezone);
                return (
                  <span className="truncate-2 text-break" title={msg}>
                    {msg?.length > 100 ? `${msg?.substring(0, 100)}...` : msg}
                  </span>
                );
              })()}
            </div>
          )}
        </div>
      </>
    );
  };

  const statusBodyTemplate = (rowData) => {
    const sanitizedSk = rowData?.sk?.replace(/[^a-zA-Z0-9_-]/g, '_');
    return (
      <>
        <span className="p-column-title text-break">Status</span>
        <div className="d-inline-table message-status">
          {rowData?.is_new ? (
            <>
              <i id={`new-message-notification-${sanitizedSk}`} className="icon-messages me-3 new-message-notification">
                <i className="icon-window-filled"></i>
              </i>
              <Tooltip target={`#new-message-notification-${sanitizedSk}`} content="You have unread replies in the message thread" />
            </>
          ) : (
            <>
              <i id={`disbaled-message-icon-${sanitizedSk}`} className="icon-messages me-3 icon-disable disabled-message-icon"></i>
              <Tooltip target={`#disbaled-message-icon-${sanitizedSk}`} content="No new replies in message thread" />
            </>
          )}
          {!isFromClientPortal &&
            (rowData?.is_new_sidebar_message ? (
              <>
                <i id={`new-sidebar-notification-${sanitizedSk}`} className="icon-sidebar new-sidebar-notification me-3">
                  <i className="icon-window-filled"></i>
                </i>
                <Tooltip target={`#new-sidebar-notification-${sanitizedSk}`} content="You have unread messages in sidebar chat" />
              </>
            ) : (
              <>
                <i id={`disbaled-sidebar-icon-${sanitizedSk}`} className="icon-sidebar icon-disable disabled-sidebar-icon me-3"></i>
                <Tooltip target={`#disbaled-sidebar-icon-${sanitizedSk}`} content="No new unread messages in sidebar chat" />
              </>
            ))}
          {!isFromClientPortal && rowData?.resolution_status && !rowData?.resolved_status && (
            <>
              <i id={`need-resolution-${sanitizedSk}`} class="bi bi-exclamation-circle me-3"></i>
              <Tooltip target={`#need-resolution-${sanitizedSk}`} content="Need resolution" />
            </>
          )}
          {!isFromClientPortal && rowData?.resolved_status && (
            <>
              <i id={`resolved-${sanitizedSk}`} class="bi bi-check-circle icon-disable me-3"></i>
              <Tooltip target={`#resolved-${sanitizedSk}`} content="Resolved" />
            </>
          )}
        </div>
      </>
    );
  };

  const sentimentBody = (rowData) => {
    const params = {
      type: rowData?.aggregate_sentiment,
      positive: rowData?.positive_sentiment_count,
      neutral: rowData?.neutral_sentiment_count,
      negative: rowData?.negative_sentiment_count,
      mixed: rowData?.mixed_sentiment_count,
    };
    return (
      <>
        <span className="p-column-title text-break">Sentiment</span>
        <div className="d-inline-table">
          <SentimentDot {...params} />
        </div>
      </>
    );
  };

  const onClickAttachments = async (e, fileInfo) => {
    e.preventDefault();
    const link = await getSignedUrl(fileInfo);
    downloadFile(link, fileInfo.display_name);
  };

  const paginatorTemplate = {
    layout: 'RowsPerPageDropdown PrevPageLink NextPageLink',
    RowsPerPageDropdown: (options) => {
      return (
        <React.Fragment>
          <span className="mx-1" style={{ color: 'var(--black-500)', userSelect: 'none' }}>
            Item Per Page
          </span>
          <Dropdown value={options.value} options={paginationDropdownOptions} onChange={onPageChange} />
        </React.Fragment>
      );
    },
    NextPageLink: ({ iconClassName }) => {
      return (
        <Button
          onClick={onNextPageClick}
          disabled={next_Token === null && isForward}
          className={'p-paginator-next p-paginator-element p-link'}
          icon={iconClassName}
        />
      );
    },
    PrevPageLink: ({ iconClassName }) => {
      return (
        <Button
          onClick={onPrevPageClick}
          disabled={transition_Token === null || (transition_Token && next_Token === null && !isForward)}
          className={'p-paginator-next p-paginator-element p-link'}
          icon={iconClassName}
        />
      );
    },
  };

  const onNextPageClick = () => {
    let obj = {
      case_id: filterCaseId,
      author_id: filterAuthorId,
      message_date: false,
      is_read: filterMsgStatus === 'scheduled' ? '' : filterMsgStatus,
      scheduled_status: filterMsgStatus === 'scheduled' ? filterMsgStatus : '',
      keyword: false,
      participant: filterParticipant,
      sentiment_filter: filterSentiment,
      pageLimit: pageLimit,
      pagination_token: !isForward ? transition_Token : next_Token,
      pagination_direction: 'forward',
      ...pickExtractedDate(dateForFilter),
    };
    fetchCaseMessage(obj);
    setIsForward(true);
  };

  const onPrevPageClick = () => {
    let obj = {
      case_id: filterCaseId,
      author_id: filterAuthorId,
      message_date: false,
      is_read: filterMsgStatus === 'scheduled' ? '' : filterMsgStatus,
      scheduled_status: filterMsgStatus === 'scheduled' ? filterMsgStatus : '',
      keyword: false,
      participant: filterParticipant,
      sentiment_filter: filterSentiment,
      pageLimit: pageLimit,
      pagination_token: isForward ? transition_Token : next_Token,
      pagination_direction: 'reverse',
      ...pickExtractedDate(dateForFilter),
    };
    fetchCaseMessage(obj);
    setIsForward(false);
  };
  const onPageChange = (event) => {
    setPageLimit(event.value);
    let path = getFilePathForPagination();
    handlePageNumberInPagination(userContext, path?.module, path?.subModule, path?.listName, event.value);
    let obj = {
      case_id: filterCaseId?.value ? filterCaseId?.value : filterCaseId,
      author_id: filterAuthorId,
      message_date: false,
      is_read: filterMsgStatus === 'scheduled' ? '' : filterMsgStatus,
      scheduled_status: filterMsgStatus === 'scheduled' ? filterMsgStatus : '',
      keyword: false,
      participant: filterParticipant,
      sentiment_filter: filterSentiment,
      pageLimit: event.value,
      pagination_token: '',
      pagination_direction: 'forward',
      ...pickExtractedDate(dateForFilter),
    };
    fetchCaseMessage(obj);
  };

  const pickExtractedDate = (dateArray) => {
    return {
      message_date: dateArray?.[0] ? convertToTimezoneFormat(moment(dateArray[0]).startOf('day'), userDetails.timezone) : '',
      to_limit: dateArray?.[0]
        ? convertToTimezoneFormat(moment(dateArray[1] ? dateArray[1] : dateArray[0]).endOf('day'), userDetails.timezone)
        : '',
    };
  };

  useEffect(() => {
    let obj = {
      case_id: filterCaseId,
      author_id: filterAuthorId,
      is_read: filterMsgStatus === 'scheduled' ? '' : filterMsgStatus,
      scheduled_status: filterMsgStatus === 'scheduled' ? filterMsgStatus : '',
      keyword: '',
      participant: filterParticipant,
      sentiment_filter: filterSentiment,
      ...pickExtractedDate(isGlobalMessagesScreen ? userDetails?.user_preferences?.filters?.secure_messages?.date : []),
    };
    fetchCaseMessage(obj);
  }, [websocketContext?.secureMessageData]);

  return (
    <div className="overflow-auto bg-white shadow-middle mb-3">
      <div className="datatable-responsive">
        <ResizableDataTable
          value={caseMessages.message}
          breakpoint="960px"
          responsiveLayout="scroll"
          className="cursor-pointer p-datatable-responsive"
          emptyMessage="No Data Found."
          paginatorTemplate={paginatorTemplate}
          rows={pageLimit}
          stripedRows
          paginator
          resizableColumns={!isFromClientPortal}
          columns={[
            { field: 'subject', header: 'Subject', body: subjectBodyTemplate, sortable: true },
            {
              field: 'author_details.first_name',
              header: 'Author',
              body: authorBodyTemplate,
              sortable: true,
              style: { cursor: 'text' },
            },
            {
              field: 'case_name',
              header: 'Case Name',
              body: caseNameBodyTemplate,
              sortable: true,
              bodyClassName: 'ellipsis-text',
              hidden: !isGlobalMessagesScreen,
            },
            { field: 'is_new', header: 'Status', body: statusBodyTemplate, sortable: true },
            { field: 'last_reply', header: 'Most Recent Replies', body: repliesTemplate, sortable: true },
            { field: 'attachment', header: 'Attachments', body: attachmentsBodyTemplate, sortable: true, bodyClassName: 'ellipsis-text' },
            {
              field: 'aggregate_sentiment',
              header: 'Sentiment',
              body: sentimentBody,
              sortable: true,
              className: 'md-align-center',
              headerStyle: { textAlign: 'center' },
              hidden: isFromClientPortal,
            },
            { field: 'is_read', header: '', body: stateBodyTemplate, bodyClassName: 'ellipsis-text' },
          ]}
          storageKey="message_list_column_widths"
        />
      </div>
    </div>
  );
}

export default memo(MessageListTable);
